import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuMapPin } from 'react-icons/lu'
import { Box, Flex, Text } from '@chakra-ui/react'
import { tryParseAsNumber } from '@leid/helpers'
import { useRouter } from 'next/router'

import { SortingInfo } from 'appTypes/mapper'
import { SelectOption } from 'components/form/Select/useSelect'
import { SelectWithSearch } from 'components/form/SelectWithSearch'
import { LeidButton } from 'components/ui/Buttons/Button'
import { CardActionButtonWithoutFill } from 'components/ui/ItemCard/styles'
import { Loader } from 'components/ui/Loader'
import LeidModal from 'components/ui/Modal/LeidModal'
import apiClient from 'helpers/clients/apiClient'
import { useCustomerInfo } from 'hooks/useCustomerInfo'
import theme from 'styles/theme'
import { CoordinatesCookieData } from 'utils/cookies'

type LocationInfo = {
  error: boolean
  lat: number | undefined
  long: number | undefined
}

type Props = {
  sortingInfo: SortingInfo | null
}

const SortButton = ({ sortingInfo }: Props): JSX.Element => {
  const { t } = useTranslation(['common', 'auth'])
  const { customerInfo } = useCustomerInfo()
  const router = useRouter()

  const isLoggedIn = customerInfo.customer !== undefined

  const shouldBeOpenedOnOpen = (): boolean => {
    if (!isLoggedIn && sortingInfo === null) {
      return true
    }

    return false
  }

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState<SelectOption | undefined>(undefined)
  const [searchOptions, setSearchOptions] = useState<SelectOption[]>([])
  const [locationLoading, setLocationLoading] = useState(false)
  const [currentLocation, setCurrentLocation] = useState<LocationInfo | undefined>(undefined)

  useEffect(() => {
    setIsModalOpen(shouldBeOpenedOnOpen())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleModalOpen = () => {
    setSelectedOption(undefined)
    setSearchOptions([])
    setLocationLoading(false)
    setCurrentLocation(undefined)

    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
  }

  const handleSearchTermChange = async (search: string) => {
    const response = await apiClient.searchLocations(search)
    if (response.success && response.data) {
      const options = response.data.map<SelectOption>((s) => ({
        label: s.text,
        value: coordinatesToString(s.lat, s.long),
      }))
      setSearchOptions(options)
      return
    }

    setSearchOptions([])
  }

  const coordinatesToString = (lat: number | undefined, long: number | undefined): string => {
    return `${lat ?? ''}:${long ?? ''}`
  }

  const stringToCoordinates = (value: string): { lat: number | undefined; long: number | undefined } => {
    const parts = value.split(':')
    if (parts.length !== 2) {
      return { lat: undefined, long: undefined }
    }

    const lat = tryParseAsNumber(parts[0])
    const long = tryParseAsNumber(parts[1])
    return { lat, long }
  }

  const onInputSelectChange = (value: SelectOption) => {
    setSelectedOption(value)
    setCurrentLocation(undefined)
    handleRedirect(undefined, value)
  }

  const handlePinClick = () => {
    setLocationLoading(true)
    if (navigator && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          const { latitude, longitude } = coords
          setCurrentLocation({
            error: false,
            lat: latitude,
            long: longitude,
          })

          setSelectedOption(undefined)
          setLocationLoading(false)

          handleRedirect(
            {
              error: false,
              lat: latitude,
              long: longitude,
            },
            undefined,
          )
        },
        (error) => {
          console.log(`Error on getting location`, error)
          setCurrentLocation({
            error: true,
            lat: undefined,
            long: undefined,
          })
          setLocationLoading(false)
        },
        { maximumAge: 60000, timeout: 1000 },
      )
    } else {
      setLocationLoading(false)
    }
  }

  const handleRedirect = async (
    currentLocation: LocationInfo | undefined,
    selectedOption: SelectOption | undefined,
  ) => {
    let coordinatesData: CoordinatesCookieData | undefined

    if (currentLocation && !currentLocation.error && currentLocation.lat && currentLocation.long) {
      coordinatesData = {
        lat: currentLocation.lat,
        long: currentLocation.long,
        label: undefined,
      }
    }

    if (selectedOption && selectedOption.value) {
      const { lat, long } = stringToCoordinates(selectedOption.value.toString())

      coordinatesData = {
        lat: lat,
        long: long,
        label: selectedOption.label,
      }
    }

    if (coordinatesData) {
      const response = await apiClient.setCustomerCoordinates(coordinatesData)
      if (response.success && response.data) {
        router.push({
          pathname: router.pathname,
          query: router.query,
        })
        setIsModalOpen(false)
      }
    }
  }

  const redirectToLoginPage = () => {
    router.push({
      pathname: '/login',
      query: {
        redirectTo: router.asPath,
      },
    })
  }

  return (
    <>
      <LeidModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        header={t('common:changeLocation')}
        size={{ base: 'cover', md: 'lg' }}
        scrollBehavior="outside"
      >
        <Flex marginBottom="10px" direction="row" gap={5} alignItems="center">
          <Text fontSize="md">{t('common:closeToYouLocation')}:</Text>
          <CardActionButtonWithoutFill onClick={handlePinClick}>
            {locationLoading ? (
              <Loader size="md" />
            ) : (
              <Flex color="green.a200">
                <LuMapPin color="green.a200" size={24} />
              </Flex>
            )}
          </CardActionButtonWithoutFill>
          <Box>
            {currentLocation && (currentLocation.error ? t('common:errorOnGettingTryAgain') : t('common:haveLocation'))}
          </Box>
        </Flex>

        <Box marginBottom="10px">
          <Text color="green.a200" marginBottom="5px">
            {t('common:searchInNorway')}
          </Text>

          <SelectWithSearch
            name="select"
            defaultOption={selectedOption}
            options={searchOptions}
            onSearchTermChange={handleSearchTermChange}
            onChange={onInputSelectChange}
            placeholder={t('common:placeholderByPostCode')}
            zIndex={theme.token.var('zIndex.max')}
          />
        </Box>

        {false && !isLoggedIn && (
          <Flex marginTop="30px" marginBottom="10px" direction="row" justifyContent="flex-end">
            <LeidButton onClick={redirectToLoginPage}>{t('auth:login')}</LeidButton>
          </Flex>
        )}
      </LeidModal>

      <Flex
        color="green.a200"
        direction="row"
        alignItems="center"
        _hover={{ textDecorationLine: 'underline', color: 'green.a250' }}
        cursor="pointer"
        onClick={handleModalOpen}
        gap={2}
      >
        <Box>
          <LuMapPin size={24} />
        </Box>
        <Text wordBreak="break-word">
          {sortingInfo ? (sortingInfo.multilang ? t(sortingInfo.text) : sortingInfo.text) : ''}
        </Text>
      </Flex>
    </>
  )
}

export default SortButton
